/* eslint-disable */
import { RACE_CIRCUITS } from '@/constants/raceCircuits'
import { MEMORY_CONFIG } from '@/constants/memoryConfig'

export class VirtualRaceSimulator {
  // Pre-calculated race outcome pools for different matchup types
  static raceOutcomePools = new Map()
  static poolsInitialized = false

  static initializePools() {
    if (this.poolsInitialized) return

    // Create outcome pools for different stat ranges
    const statRanges = [
      { min: 0, max: 50, label: 'novice' },
      { min: 51, max: 100, label: 'intermediate' },
      { min: 101, max: 150, label: 'advanced' },
      { min: 151, max: 200, label: 'expert' },
      { min: 201, max: 300, label: 'master' }
    ]

    const raceTypes = ['short', 'medium', 'long']

    // Generate outcome distributions for each combination
    statRanges.forEach(range => {
      raceTypes.forEach(raceType => {
        const key = `${range.label}-${raceType}`
        this.raceOutcomePools.set(key, this.generateOutcomePool(range, raceType))
      })
    })

    this.poolsInitialized = true
  }

  static generateOutcomePool(statRange, raceType) {
    const pool = []
    const poolSize = MEMORY_CONFIG.virtualRacePoolSize

    for (let i = 0; i < poolSize; i++) {
      // Generate realistic time distributions based on stat range
      const baseTime = this.getBaseTime(raceType, statRange)
      const variance = baseTime * 0.05 // 5% variance
      const time = baseTime + (Math.random() - 0.5) * 2 * variance

      pool.push({
        time: time,
        variance: Math.random() * 0.02 // Additional 2% variance for individual performance
      })
    }

    // Sort pool by time for easier position determination
    pool.sort((a, b) => a.time - b.time)
    return pool
  }

  static getBaseTime(raceType, statRange) {
    // Use same base times as detailed background races for consistency
    const detailedBaseTimes = {
      short: 43.2,
      medium: 105.0,
      long: 340.0
    }
    
    const baseTime = detailedBaseTimes[raceType]
    
    // Apply scaling based on stat range to maintain competitive differences
    const scaleFactors = {
      novice: 2.0,    // 2x slower than base (worst chocobos)
      intermediate: 1.6,  // 1.6x slower than base
      advanced: 1.3,  // 1.3x slower than base
      expert: 1.1,   // 1.1x slower than base
      master: 1.0    // Base time (best chocobos)
    }
    
    return baseTime * (scaleFactors[statRange.label] || 1.0)
  }

  static simulateVirtualRace(participants, raceType, circuit) {
    if (!this.poolsInitialized) {
      this.initializePools()
    }

    // Convert full chocobo objects to lightweight representations
    const lightParticipants = participants.map(p => this.toLightweight(p))

    // Group participants by stat range
    const grouped = this.groupByStatRange(lightParticipants, raceType)

    // Generate results using pre-calculated pools
    const results = []
    
    grouped.forEach(group => {
      const pool = this.raceOutcomePools.get(`${group.range}-${raceType}`)
      
      group.participants.forEach(participant => {
        // Pick a random outcome from the pool
        const poolIndex = Math.floor(Math.random() * pool.length)
        const baseOutcome = pool[poolIndex]
        
        // Apply individual modifiers
        const modifier = this.calculateIndividualModifier(participant)
        const finalTime = baseOutcome.time * modifier

        results.push({
          chocoboId: participant.id,
          time: finalTime,
          baseStats: participant.effectiveStats
        })
      })
    })

    // Sort by time and assign positions
    results.sort((a, b) => a.time - b.time)
    results.forEach((result, index) => {
      result.position = index + 1
    })

    return this.createRaceRecord(results, raceType, circuit)
  }

  static toLightweight(chocobo) {
    // Extract only essential properties for race simulation
    return {
      id: chocobo.id,
      speed: chocobo.speed || 0,
      stamina: chocobo.stamina || 0,
      level: chocobo.level || 1,
      behaviorId: chocobo.behaviorId || 3,
      hasSpeedXFactor: chocobo.hasSpeedXFactor || false,
      hasStaminaXFactor: chocobo.hasStaminaXFactor || false
    }
  }

  static groupByStatRange(participants, raceType) {
    const groups = new Map()

    participants.forEach(p => {
      const effectiveStats = this.calculateEffectiveStats(p, raceType)
      p.effectiveStats = effectiveStats

      let range = 'novice'
      if (effectiveStats > 200) range = 'master'
      else if (effectiveStats > 150) range = 'expert'
      else if (effectiveStats > 100) range = 'advanced'
      else if (effectiveStats > 50) range = 'intermediate'

      if (!groups.has(range)) {
        groups.set(range, { range, participants: [] })
      }
      groups.get(range).participants.push(p)
    })

    return Array.from(groups.values())
  }

  static calculateEffectiveStats(participant, raceType) {
    const speedWeight = raceType === 'short' ? 0.7 : raceType === 'medium' ? 0.5 : 0.3
    const staminaWeight = 1 - speedWeight

    let effectiveStats = (participant.speed * speedWeight) + (participant.stamina * staminaWeight)

    // Apply X-factor bonuses
    if (participant.hasSpeedXFactor && speedWeight > 0.5) {
      effectiveStats *= 1.1
    }
    if (participant.hasStaminaXFactor && staminaWeight > 0.5) {
      effectiveStats *= 1.1
    }

    return effectiveStats
  }

  static calculateIndividualModifier(participant) {
    let modifier = 1.0

    // Behavior impact (simplified)
    const behaviorModifiers = {
      1: 0.95, // Disciplined
      2: 0.97, // Spirited
      3: 1.0,  // Steady
      4: 1.02, // Cautious
      5: 1.05, // Stubborn
      6: 1.08, // Skittish
      7: 1.12, // Rebellious
      8: 1.15  // Feral
    }

    modifier *= behaviorModifiers[participant.behaviorId] || 1.0

    // Random daily variance (±3%)
    modifier *= (0.97 + Math.random() * 0.06)

    return modifier
  }

  static createRaceRecord(results, raceType, circuit) {
    return {
      type: 'virtual',
      raceType: raceType,
      circuit: circuit,
      date: new Date().toISOString(),
      results: results.map(r => ({
        chocoboId: r.chocoboId,
        position: r.position,
        time: r.time
      })),
      // Minimal data for background races
      isBackground: true
    }
  }

  static simulateBatchRaces(raceConfigs, gameStore) {
    const allResults = []

    raceConfigs.forEach(config => {
      const { participants, raceType, circuit } = config
      
      // Charge entry fees to all participants' owners
      participants.forEach(chocobo => {
        const owner = gameStore.owners.find(o => o.chocoboIds.includes(chocobo.id))
        if (owner && owner.money >= circuit.entryFee) {
          owner.money -= circuit.entryFee
        }
      })
      
      // Use virtual simulation for large batches
      if (participants.length > 4 || config.forceVirtual) {
        const result = this.simulateVirtualRace(participants, raceType, circuit)
        allResults.push(result)
      } else {
        // Fall back to detailed simulation for small, important races
        // This would use the existing detailed race simulator
        allResults.push(null) // Placeholder for detailed sim
      }
    })

    return allResults.filter(r => r !== null)
  }

  static updateChocoboRecords(results, gameStore) {
    // Batch update chocobo records efficiently
    const updates = new Map()

    results.forEach(race => {
      race.results.forEach(result => {
        if (!updates.has(result.chocoboId)) {
          updates.set(result.chocoboId, [])
        }
        
        // Calculate earnings for virtual race
        let earnings = 0
        if (result.position <= 3) {
          const baseReward = {
            1: 4000,
            2: 2000,
            3: 1000
          }[result.position]
          earnings = Math.floor(baseReward * race.circuit.rewardMultiplier)
          
          // Award money to chocobo's owner
          const owner = gameStore.owners.find(o => o.chocoboIds.includes(result.chocoboId))
          if (owner) {
            owner.money += earnings
          }
        }

        updates.get(result.chocoboId).push({
          position: result.position,
          circuit: race.circuit,
          raceType: race.raceType,
          finishTime: result.time,
          day: gameStore.currentDay,
          earnings: earnings
        })
      })
    })

    // Apply updates in batch
    updates.forEach((races, chocoboId) => {
      const chocobo = gameStore.chocobos.find(c => c.id === chocoboId)
      if (chocobo) {
        // Add races to history (respecting limits)
        races.forEach(race => {
          chocobo.recordRaceResult(
            race.position,
            race.circuit,
            race.raceType,
            race.finishTime,
            race.day,
            race.earnings || 0
          )
        })

        // Update experience for wins
        const wins = races.filter(r => r.position === 1).length
        if (wins > 0) {
          chocobo.gainExperience(wins * 25) // Reduced XP for background races
        }
      }
    })
  }

  static generateDailyRaceSchedule(gameStore) {
    const schedule = []
    
    // Initialize monthly race tracking if not exists
    if (!gameStore.monthlyRaceCount) {
      gameStore.monthlyRaceCount = this.initializeMonthlyRaceCount()
      gameStore.lastRaceCountReset = Math.floor(gameStore.currentDay / 32)
    }
    
    // Reset monthly count at start of new month
    const currentMonth = Math.floor(gameStore.currentDay / 32)
    if (currentMonth > gameStore.lastRaceCountReset) {
      gameStore.monthlyRaceCount = this.initializeMonthlyRaceCount()
      gameStore.lastRaceCountReset = currentMonth
    }
    
    // Distribute daily races across tiers based on monthly limits
    const dailyRaceDistribution = this.calculateDailyRaceDistribution(
      gameStore.monthlyRaceCount, 
      gameStore.currentDay % 32 + 1 // Day within current month
    )
    
    // Generate races for each tier
    Object.entries(dailyRaceDistribution).forEach(([circuitId, raceCount]) => {
      if (raceCount <= 0) return
      
      const circuit = Object.values(RACE_CIRCUITS).find(c => c.id === circuitId)
      if (!circuit) return
      
      for (let i = 0; i < raceCount; i++) {
        const raceType = this.selectRaceType()
        const participants = this.selectParticipants(gameStore, circuit, raceType)
        
        if (participants.length >= 4) {
          schedule.push({
            circuit,
            raceType,
            participants,
            forceVirtual: true
          })
          
          // Track the race
          gameStore.monthlyRaceCount[circuitId]++
        }
      }
    })

    return schedule
  }
  
  static initializeMonthlyRaceCount() {
    return {
      saucer: 0,
      global: 0,
      transcontinental: 0,
      continental: 0,
      regional: 0
    }
  }
  
  static calculateDailyRaceDistribution(monthlyCount, dayOfMonth) {
    const monthlyLimits = MEMORY_CONFIG.monthlyRaceLimits
    const daysInMonth = 32
    const racesRemaining = Math.max(0, MEMORY_CONFIG.backgroundRacesPerDay - 5) // Reserve 5 for guaranteed regional
    
    // Calculate how many races of each tier we can still run this month
    const remainingRaces = {
      saucer: Math.max(0, monthlyLimits.saucer - monthlyCount.saucer),
      global: Math.max(0, monthlyLimits.global - monthlyCount.global),
      transcontinental: Math.max(0, monthlyLimits.transcontinental - monthlyCount.transcontinental),
      continental: Math.max(0, monthlyLimits.continental - monthlyCount.continental),
      regional: Math.max(0, monthlyLimits.regional - monthlyCount.regional)
    }
    
    // Calculate days remaining in month
    const daysRemaining = daysInMonth - dayOfMonth + 1
    
    // Distribute high-tier races evenly across remaining days
    const dailyDistribution = {
      saucer: Math.min(1, Math.ceil(remainingRaces.saucer / daysRemaining)),
      global: Math.min(1, Math.ceil(remainingRaces.global / daysRemaining)),
      transcontinental: Math.min(2, Math.ceil(remainingRaces.transcontinental / daysRemaining)),
      continental: Math.min(4, Math.ceil(remainingRaces.continental / daysRemaining)),
      regional: 5 // Always guarantee at least 5 regional races per day
    }
    
    // Add chance-based selection for high-tier races to spread them out
    const saucerRaces = remainingRaces.saucer > 0 && Math.random() < (remainingRaces.saucer / daysRemaining) ? 1 : 0
    const globalRaces = remainingRaces.global > 0 && Math.random() < (remainingRaces.global / daysRemaining) ? 1 : 0
    const transRaces = Math.floor(Math.random() * (dailyDistribution.transcontinental + 1))
    const contRaces = Math.floor(Math.random() * (dailyDistribution.continental + 1))
    
    const finalDistribution = {
      saucer: saucerRaces,
      global: globalRaces,
      transcontinental: transRaces,
      continental: contRaces,
      regional: Math.max(5, MEMORY_CONFIG.backgroundRacesPerDay - saucerRaces - globalRaces - transRaces - contRaces)
    }
    
    return finalDistribution
  }

  static getCircuitActivityLevel(circuit) {
    // Higher tier circuits have fewer but more important races
    const activityLevels = {
      regional: 15,
      continental: 10,
      transcontinental: 7,
      global: 5,
      goldSaucer: 3
    }

    return activityLevels[circuit.id] || 5
  }

  static selectRaceType() {
    const rand = Math.random()
    if (rand < 0.4) return 'short'
    if (rand < 0.7) return 'medium'
    return 'long'
  }

  static selectParticipants(gameStore, circuit, raceType) {
    // Select eligible NPC chocobos only - NEVER include player chocobos in background races
    const npcOwners = gameStore.owners.filter(owner => !owner.isPlayer)
    
    // Filter owners who can afford the entry fee
    const eligibleOwners = npcOwners.filter(owner => owner.money >= circuit.entryFee)
    
    const npcChocobos = eligibleOwners.flatMap(owner => 
      gameStore.getChocobosByOwnerId(owner.id)
    )
    
    const eligible = npcChocobos.filter(c => 
      c.level >= circuit.unlockLevel &&
      !c.inTransit
      // Region check removed for now - would need proper region mapping
    )

    // Randomly select 4-8 participants
    const count = Math.floor(Math.random() * 5) + 4
    const selected = []

    while (selected.length < count && eligible.length > selected.length) {
      const index = Math.floor(Math.random() * eligible.length)
      const chocobo = eligible[index]
      
      if (!selected.find(s => s.id === chocobo.id)) {
        selected.push(chocobo)
      }
    }

    return selected
  }
}